import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { formatTimeSaudi } from '../../utils/saudiTime';

function parseYyyyMmDdLocalStartMs(dateStr) {
  const m = String(dateStr || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  if (!y || mo < 0 || mo > 11 || !d) return null;
  return new Date(y, mo, d, 0, 0, 0, 0).getTime();
}

function parseYyyyMmDdLocalEndMs(dateStr) {
  const m = String(dateStr || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  if (!y || mo < 0 || mo > 11 || !d) return null;
  return new Date(y, mo, d, 23, 59, 59, 999).getTime();
}

function rowMatchesSearch(r, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const parts = [
    r.orderNo,
    r.customerName,
    r.customerNo,
    r.customerId != null ? String(r.customerId) : '',
    r.order != null ? String(r.order) : '',
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return parts.some((p) => p.includes(needle));
}

export default function SalesCustomerDebt() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.orders.listSalesCustomerDebts({ limit: 500 });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load customer debt');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const fromMs = dateFrom ? parseYyyyMmDdLocalStartMs(dateFrom) : null;
    const toMs = dateTo ? parseYyyyMmDdLocalEndMs(dateTo) : null;

    return rows.filter((r) => {
      if (!rowMatchesSearch(r, search)) return false;
      if (fromMs != null || toMs != null) {
        const t = r.createdAt ? new Date(r.createdAt).getTime() : NaN;
        if (!Number.isFinite(t)) return false;
        if (fromMs != null && t < fromMs) return false;
        if (toMs != null && t > toMs) return false;
      }
      return true;
    });
  }, [rows, search, dateFrom, dateTo]);

  const hasActiveFilters = search.trim() !== '' || dateFrom !== '' || dateTo !== '';

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-amber-500 p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-700">Customer debt</h1>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        <div className="mb-4 p-3 sm:p-4 border border-amber-200 rounded-lg bg-amber-50/50 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="debt-search">
                Search (customer name, customer ID, order number, order ID)
              </label>
              <input
                id="debt-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type name or any ID…"
                disabled={loading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="debt-from">
                From date
              </label>
              <input
                id="debt-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={loading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="debt-to">
                To date
              </label>
              <input
                id="debt-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={loading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600">
              {!loading && (
                <>
                  Showing <span className="font-semibold text-gray-900">{filteredRows.length}</span> of{' '}
                  <span className="font-semibold text-gray-900">{rows.length}</span> record(s)
                </>
              )}
              {loading && <span className="text-sm text-gray-500">Loading…</span>}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm font-medium text-amber-800 hover:text-amber-950 underline"
              >
                Clear search &amp; dates
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        {!loading && rows.length === 0 && (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">No debt records yet.</div>
        )}

        {!loading && rows.length > 0 && filteredRows.length === 0 && (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">
            No records match your search or filters.
          </div>
        )}

        {!loading && filteredRows.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 whitespace-nowrap">Date</th>
                  <th className="px-3 py-2 whitespace-nowrap">Order</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2 whitespace-nowrap">Status</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Bill</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Collected</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Balance</th>
                  <th className="px-3 py-2 whitespace-nowrap">Recorded by</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {r.createdAt ? formatTimeSaudi(r.createdAt) : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-900 whitespace-nowrap">{r.orderNo || '—'}</td>
                    <td className="px-3 py-2 text-gray-800">
                      <div className="font-medium">{r.customerName || '—'}</div>
                      {r.customerNo ? (
                        <div className="text-xs text-gray-500 font-mono">{r.customerNo}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${
                          String(r.status || 'open').toLowerCase() === 'settled'
                            ? 'bg-slate-100 text-slate-800 border-slate-200'
                            : 'bg-amber-50 text-amber-900 border-amber-200'
                        }`}
                      >
                        {String(r.status || 'open').toLowerCase() === 'settled' ? 'Settled' : 'Open'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      SAR {Number(r.billTotal || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      SAR {Number(r.amountCollected || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-800">
                      SAR {Number(r.balanceDue || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{r.recordedByName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
